/**
 * The one vocabulary for trade outcomes, directions and partner intent, so a
 * trade reads and colours identically on the log, the inspection and the
 * partner timeline. Formatting helpers for the same screens live here too.
 */
import type { BadgeTone } from '/imports/ui/Badge';
import type { TradeIntent } from '/imports/api/analytics';

export interface KindVocab { id: string; label: string; tone: BadgeTone; bar: string; blurb: string }

/** Best to worst. completed = good; no_sale = lost at the finish line (the
 *  worst miss); no_buy = they declined our offer; no_offer / cancelled =
 *  nothing to act on, in two greys so the bar still separates them. */
export const KINDS: KindVocab[] = [
  { id: 'completed', label: 'Completed', tone: 'green', bar: 'bg-primary', blurb: 'Finalised' },
  { id: 'no_sale', label: 'No sale', tone: 'red', bar: 'bg-sell', blurb: 'A deal existed but never completed' },
  { id: 'no_buy', label: 'No buy', tone: 'amber', bar: 'bg-warn', blurb: 'Bot offered, partner did not engage' },
  { id: 'no_offer', label: 'No offer', tone: 'neutral', bar: 'bg-fg-3/60', blurb: 'Bot looked and wanted nothing' },
  { id: 'cancelled', label: 'Cancelled', tone: 'neutral', bar: 'bg-fg-3/30', blurb: 'Died before the bot evaluated the binder' },
];
export const kindVocab = (id: string): KindVocab => KINDS.find((k) => k.id === id) ?? { id, label: id, tone: 'neutral', bar: 'bg-fg-3/30', blurb: '' };

export const INTENT_TONE: Record<TradeIntent, BadgeTone> = { seller: 'green', buyer: 'amber', hybrid: 'blue', unknown: 'neutral' };
export const INTENT_DOT: Record<TradeIntent, string> = { seller: 'bg-primary', buyer: 'bg-warn', hybrid: 'bg-info', unknown: 'bg-fg-3' };

export const DIR_LABEL: Record<string, string> = { buy: 'We bought', sell: 'We sold', both: 'We bought & sold', none: '' };

export const RARITY_COLOR: Record<string, string> = { MYTHIC: '#E0662F', RARE: '#D9AA3C', UNCOMMON: '#8FA0AC', COMMON: '#5C6773' };

export const fmtInt = (n: number) => n.toLocaleString();
export const fmtTix = (n: number) => `${(Math.round(n * 100) / 100).toLocaleString()} tix`;
export const fmtSigned = (n: number) => `${n > 0 ? '+' : ''}${(Math.round(n * 100) / 100).toLocaleString()} tix`;
export const fmtPrice = (n: number | null) => (n == null || !Number.isFinite(n) ? '–' : (Math.round(n * 1000) / 1000).toString());
export const fmtPct = (n: number) => `${Math.round(n)}%`;
export const secs = (ms: number) => (ms >= 60000 ? `${Math.round(ms / 60000)}m` : `${Math.round(ms / 1000)}s`);
export const hhmm = (d: Date | string) => new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
export const hhmmss = (d: Date | string) => new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
export const ymd = (d: Date | string) => new Date(d).toISOString().slice(0, 10);

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
/** Whole local days between a moment and now. */
export const daysAgo = (d: Date | string) => Math.round((startOfDay(new Date()) - startOfDay(new Date(d))) / 86400000);
export const ageLabel = (d: Date | string) => {
  const n = daysAgo(d);
  return n <= 0 ? 'today' : n === 1 ? 'yesterday' : `${n}d ago`;
};
export const dayGroupLabel = (n: number) => (n <= 0 ? 'Today' : n === 1 ? 'Yesterday' : n < 21 ? `${n} days ago` : `${Math.round(n / 7)} weeks ago`);
